import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import dt, { useIsMobileOrTablet } from '../../../shared/theme';
import { Separator } from '../../shared/sectionable/styles';
import LogoSeparator from '../../shared/logo-separator';
import InfiniteScrollEndText from '../../shared/infinite-scroll-end-text';
import Disclaimer from './disclaimer';
import Card from './card';
import Actions from './actions';
import ViewOptions from './view-options';
import NoResults from './no-results';
import NameFilter from './filter-components/name-filter';
import Tail from '../../shared/feed/styles';
import FundModal from '../fund-modal';
import useGetFunds from '../../../services/funds/search-service';
import useFundsStateStore from '../common/funds-state-hook';
import { searchCriteriaToUrl, urlToSearchCriteria } from '../common/utils';

const bottomRootMargin = 600;
const loadingDelay = 10;

const Container = styled.div`
  width: 100%;
  padding-top: 56px;

  ${props => dt(props).theme.isMobileOrTablet && `
    padding-top: 40px;
  `};
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 28px;

  ${props => dt(props).theme.isXLarge && `
    padding: 0 calc(50% - 476px);
  `};

  ${props => dt(props).theme.isXXLarge && `
    padding: 0 calc(50% - 600px);
  `};

  ${props => dt(props).theme.isMobileOrTablet && `
    padding: 0 20px;
  `};
`;

const SSeparator = styled(Separator)`
  margin-top: 12px;
  margin-bottom: 24px;
`;

const Logo = styled(LogoSeparator)`
  margin: ${props => (dt(props).theme.isMobileOrTablet ? 40 : 56)}px 0;
`;

export default function Main(props) {
  const { newNavEnabled, companySlug, isCompany } = props;
  const searchCompanySlug = isCompany ? companySlug : null;
  const searchCriteria = urlToSearchCriteria();
  const [{ loading }, getFunds] = useGetFunds();
  const set = useFundsStateStore(state => state.set);
  const funds = useFundsStateStore(state => state.funds);
  const totalResults = useFundsStateStore(state => state.totalResults);
  const assetClasses = useFundsStateStore(state => state.assetClasses);
  const appliedFilterFundTypes = useFundsStateStore(state => state.appliedFilters.fundTypes);
  const appliedFilterAssetClasses = useFundsStateStore(state => state.appliedFilters.assetClasses);
  const filterName = useFundsStateStore(state => state.filters.name);
  const filterChanged = useFundsStateStore(state => state.filterChanged);
  const sortOrder = useFundsStateStore(state => state.sortOrder);
  const sortOrderChanged = useFundsStateStore(state => state.sortOrderChanged);
  const showNameFilter = useFundsStateStore(state => state.showNameFilter);
  const [showLoadMoreButton, setShowLoadMoreButton] = React.useState(false);
  const [hasNextPage, setHasNextPage] = React.useState(true);
  const [page, setPage] = React.useState(1);

  const resetFilters = React.useCallback(() => {
    set(state => {
      state.filters.assetClasses = [];
      state.filters.fundTypes = [];
      state.filters.name = '';
      state.appliedFilters.assetClasses = [];
      state.appliedFilters.fundTypes = [];
    });
  }, [set]);

  React.useEffect(() => {
    set(state => {
      state.resetFilters = resetFilters;
    });
  }, [set, resetFilters]);

  const buildFiltersAndParams = () => {
    const {
      type,
      assetFilters,
      sortOrder: urlSortOrder,
      series: urlSeries,
    } = searchCriteria;
    // eslint-disable-next-line no-nested-ternary
    const fundTypes = appliedFilterFundTypes.length > 0
      ? appliedFilterFundTypes : type ? [type] : [];

    if (assetFilters.length > 0 || urlSortOrder || urlSeries) {
      const ids = (assetClasses || [])
        .filter(ac => _.indexOf(assetFilters, ac.slug) >= 0)
        .map(ac => ac.id);

      set(state => {
        state.filters.assetClasses = [...ids];
        state.filters.fundType = fundTypes;
        state.filterChanged = true;
        state.appliedFilters.assetClasses = [...ids];
        state.appliedFilters.fundTypes = fundTypes;
        state.sortOrder = urlSortOrder || 'default';
        state.series = urlSeries;
      });
    }
  };

  const onData = React.useCallback(({ data }) => {
    if (funds.length === 0 || filterChanged || sortOrderChanged) {
      setShowLoadMoreButton(true);
      setHasNextPage(false);
      const hasMorePage = data.total > data.funds.length;
      setPage(1);
      setHasNextPage(hasMorePage);
      setShowLoadMoreButton(hasMorePage);
      set(state => {
        state.funds = data.funds;
        state.totalResults = _.get(data, 'total', 0);
        state.filterChanged = false;
        state.sortOrderChanged = false;
        state.breadcrumb = data.breadcrumb;
      });
    } else {
      const displayedFunds = [...funds, ...data.funds];
      setHasNextPage(totalResults > displayedFunds.length);
      set(state => {
        state.funds = displayedFunds;
      });
    }
  }, [funds, totalResults, filterChanged, sortOrderChanged, set]);

  // eslint-disable-next-line complexity
  useChangeAwareEffect(({ did, isMount }) => {
    const type = appliedFilterFundTypes[0];

    if (did.assetClasses.change) {
      buildFiltersAndParams();
    }

    const slugs = (assetClasses || [])
      .filter(ac => _.indexOf(appliedFilterAssetClasses, ac.id) >= 0)
      .map(ac => ac.slug);
    const newUrl = searchCriteriaToUrl(
      type,
      slugs,
      sortOrder,
      searchCriteria.series,
      searchCompanySlug || null,
    );

    if (sessionStorage.getItem('userChangedFilterCondition')) {
      sessionStorage.removeItem('userChangedFilterCondition');
      window.location = newUrl.replace(/\/$/, '');
      return;
    }

    if (isMount) {
      getFunds(type,
        sortOrder,
        page,
        appliedFilterAssetClasses,
        searchCriteria.series,
        filterName,
        searchCompanySlug).then(onData);
    } else if (
      did.appliedFilterAssetClasses.change
      || did.appliedFilterFundTypes.change
      || did.sortOrder.change
      || did.filterName.change
    ) {
      if (searchCompanySlug) {
        if (newUrl !== window.location.href) {
          window.location.href = newUrl;
        }
      }

      if (assetClasses.length > 0) {
        if (searchCompanySlug && appliedFilterAssetClasses.length > 0) {
          window.location = newUrl.replace(/\/$/, '');
        } else {
          window.history.pushState(null, document.title, newUrl.replace(/\/$/, ''));
        }
      }

      getFunds(type,
        sortOrder,
        1,
        appliedFilterAssetClasses,
        searchCriteria.series,
        filterName,
        searchCompanySlug).then(onData);
    }
  }, {
    assetClasses,
    appliedFilterAssetClasses,
    appliedFilterFundTypes,
    filterName,
    sortOrder,
    searchCompanySlug,
  });

  const onLoadMore = React.useCallback(isLoadMoreClicked => {
    if (!showLoadMoreButton || !!isLoadMoreClicked) {
      setHasNextPage(totalResults > funds.length);
      const newPage = page + 1;

      getFunds(searchCriteria.type,
        sortOrder,
        newPage,
        appliedFilterAssetClasses,
        searchCriteria.series,
        filterName,
        searchCompanySlug)
        .then(res => {
          setShowLoadMoreButton(false);
          onData({ data: res.data });
        });
      setPage(newPage);
    }
  }, [funds, totalResults, appliedFilterAssetClasses, filterName, sortOrder,
    showLoadMoreButton, page, getFunds, searchCriteria, onData, searchCompanySlug]);

  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
    rootMargin: `0px 0px ${bottomRootMargin}px 0px`,
    delayInMs: loadingDelay,
  });

  const isMobileOrTablet = useIsMobileOrTablet();
  const hasResult = totalResults > 0;

  set(state => {
    state.loadingFunds = loading;
  });

  return (
    <Container>
      <SearchContainer>
        <Actions {...props} />
        <SSeparator />
        {isMobileOrTablet && showNameFilter && <NameFilter {...props} />}
        {hasResult && (
          <>
            <ViewOptions />
            <article>
              {funds.map((fund, index) => (<Card name={`fund-${index}`} key={`fund-${fund.id}`} fund={fund} />))}
              <div ref={infiniteRef} />
              {showLoadMoreButton && (
                <Tail>
                  <button type="button" disabled={loading} onClick={() => onLoadMore(true)} className="view-more">load more results</button>
                </Tail>
              )}
              {!hasNextPage && <InfiniteScrollEndText text="You have seen all the results" />}
            </article>
            <Logo animate />
          </>
        )}
        {!loading && !hasResult && <NoResults />}
      </SearchContainer>
      {!newNavEnabled && <Disclaimer />}
      <FundModal {...props} />
    </Container>
  );
}

Main.propTypes = {
  /* eslint-disable-next-line react/require-default-props */
  newNavEnabled: PropTypes.bool,
  /* eslint-disable-next-line react/require-default-props */
  companySlug: PropTypes.string,
  isCompany: PropTypes.bool.isRequired,
};
